import React from 'react'
import styled from 'styled-components'
import PreviewCard from './PreviewCard'

// --- Styled Components ---

const Viewmodel_CardStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '164px',
    position: 'relative',
    overflow: 'hidden',
    isolation: 'isolate'
}

const Viewmodel_ContentWrapper = styled.div`
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    width: 100%;
    z-index: 10;
    align-items: center;
`

const Viewmodel_ControlsOverlay = styled.div`
    position: absolute;
    bottom: 12px;
    left: ${props => props.$isLeft ? '12px' : 'auto'};
    right: ${props => props.$isLeft ? 'auto' : '12px'};
    display: flex;
    gap: 8px;
    z-index: 10;
    background: #121b27;
    width: 100%;
    left: 0;
    bottom: 0;
    text-align: center;
    justify-content: center;
    padding: 3px;
`

const Viewmodel_ControlVal = styled.div`
    font-size: 10px;
    font-family: 'Consolas', monospace;
    font-weight: 600;
    color: rgba(255,255,255,0.5);
`

const Viewmodel_MiniPreviewBackground = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0.6) 0%,
        rgba(15, 23, 42, 0.8) 100%
    );
    background-image:
        linear-gradient(rgba(99, 102, 241, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
    background-size: 10px 10px;
    z-index: 1;
    pointer-events: none;
`

const Viewmodel_MiniGunContainer = styled.div`
    width: 100%;
    height: calc(100% - 19px);
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    overflow: hidden;
    perspective: 400px;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    transform: ${({ $isLeftHand }) => ($isLeftHand ? 'scaleX(-1)' : 'none')};
`

const Viewmodel_MiniSimpleGunBox = styled.div`
    width: 44px; /* Slightly wider */
    height: 90px; /* Much taller to be visible */
    background: linear-gradient(180deg, #64748b 0%, #475569 100%); /* Lighter slate for visibility */
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.6);
    transform-origin: 50% 100%;
    transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
    position: relative;
    margin-bottom: -35px; /* Sunk deep by default like original (-50px) */

    /* Sight indication */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 3px;
        height: 12px;
        background: rgba(255, 255, 255, 0.4);
    }
`

const Viewmodel_FloatingSideLabel = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(13, 17, 23, 0.8);
    border: 1px solid #6366f1;
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 9px;
    color: #fff;
    font-weight: 700;
    pointer-events: none;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    z-index: 5;
    text-transform: uppercase;
    font-family: var(--font-chakra);
`

const Viewmodel_FloatingFovLabel = styled.div`
    position: absolute;
    top: 8px;
    left: 8px;
    background: rgba(13, 17, 23, 0.8);
    border: 1px solid #6366f1;
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 9px;
    color: #fff;
    font-weight: 700;
    pointer-events: none;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    z-index: 5;
    text-transform: uppercase;
    font-family: var(--font-chakra);
`

const Viewmodel_MiniStatLab = styled.div`
    font-size: 8px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.3);
    font-weight: 600;
    letter-spacing: 0.8px;
    margin-bottom: 8px;
    margin-top: 14px;
`

// --- Main Component ---

export default function ViewmodelPreview({
    data, // viewmodel
    isActive,
    isDisabled, // usually !configName
    onToggle,
    onClick,
    t,
    ...props
}) {
    const vm = data || {
        viewmodel_offset_x: 1,
        viewmodel_offset_y: 1,
        viewmodel_offset_z: -1,
        viewmodel_fov: 60,
        cl_prefer_lefthanded: 0,
    }

    // Logic adapted from ViewmodelModule but scaled for mini preview
    const pixelX = (Number(vm.viewmodel_offset_x || 0) + 2.5) * 12
    const pixelY = -Number(vm.viewmodel_offset_z || 0) * 4 // Increased vertical movement

    const fovScale = 1 - (Number(vm.viewmodel_fov || 60) - 60) * 0.02
    const depthScale = 1 - Number(vm.viewmodel_offset_y || 0) * 0.05
    const totalScale = fovScale * depthScale

    // Scale compensation (more aggressive sinking)
    const scaleYComp = totalScale > 1
        ? (totalScale - 1) * 120
        : (totalScale - 1) * 25

    const depthTranslateY = -Number(vm.viewmodel_offset_y || 0) * 6 // Increased depth shift

    const finalTransform = `translate(${pixelX}px, ${pixelY + depthTranslateY + scaleYComp}px) scale(${totalScale})`
    const isLeft = vm.cl_prefer_lefthanded !== 1

    return (
        <PreviewCard
            isActive={isActive}
            isDisabled={isDisabled}
            onClick={onClick}
            onToggle={onToggle}
            label={t('VIEWMODEL_GEOMETRY')}
            style={{ ...Viewmodel_CardStyles, ...props.style }}
            {...props}
        >
            <Viewmodel_MiniPreviewBackground />

            <Viewmodel_ContentWrapper>
                <Viewmodel_MiniStatLab>{t('VIEWMODEL_GEOMETRY')}</Viewmodel_MiniStatLab>
                {/* Gun visualization */}
            </Viewmodel_ContentWrapper>

            <Viewmodel_MiniGunContainer $isLeftHand={vm.cl_prefer_lefthanded === 1}>
                <div
                    style={{
                        transform: finalTransform,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        transformOrigin: '50% 100%',
                    }}
                >
                    <Viewmodel_MiniSimpleGunBox />
                </div>
            </Viewmodel_MiniGunContainer>

            {/* Overlays */}
            <Viewmodel_FloatingFovLabel>
                FOV: {!isDisabled ? (vm.viewmodel_fov || 60) : '-'}
            </Viewmodel_FloatingFovLabel>
            <Viewmodel_FloatingSideLabel>
                {!isDisabled ? (vm.cl_prefer_lefthanded === 1 ? 'LEFT' : 'RIGHT') : '-'}
            </Viewmodel_FloatingSideLabel>

            <Viewmodel_ControlsOverlay $isLeft={isLeft}>
                <Viewmodel_ControlVal>
                    <span style={{ color: '#ff4b4b' }}>X</span> {!isDisabled ? vm.viewmodel_offset_x : '-'}
                </Viewmodel_ControlVal>
                <Viewmodel_ControlVal>
                    <span style={{ color: '#3fdc46' }}>Y</span> {!isDisabled ? vm.viewmodel_offset_y : '-'}
                </Viewmodel_ControlVal>
                <Viewmodel_ControlVal>
                    <span style={{ color: '#6366f1' }}>Z</span> {!isDisabled ? vm.viewmodel_offset_z : '-'}
                </Viewmodel_ControlVal>
            </Viewmodel_ControlsOverlay>
        </PreviewCard>
    )
}
